// apps/web/src/pages/attendance/PermessiPage.tsx
// Pagina permessi — tecnico: calendario + lista proprie richieste + nuova richiesta
//                 — team_leader: idem + calendario di gruppo + selezione referente admin
import { useState, useMemo, useRef } from 'react' // useRef per pastInputRef
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ChevronLeft, ChevronRight, CheckCircle2, Clock } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Input } from '../../components/ui/Input'
import { useAuth } from '../../hooks/useAuth'
import api from '../../lib/api'

interface LeaveType {
  id: string
  name: string
  color: string
  requiresApproval: boolean
}

interface LeaveRequest {
  id: string
  technicianId: string
  dateFrom: string
  dateTo: string
  notes: string | null
  status: 'pending' | 'approved' | 'rejected'
  rejectionReason: string | null
  leaveType: { id: string; name: string; color: string }
  technician?: { id: string; firstName: string; lastName: string }
}

interface AttendanceRecord {
  id: string
  technicianId: string
  date: string          // 'YYYY-MM-DD'
  workedMinutes: number
  source: string        // 'ticket' | 'manual' | 'mixed'
  technician?: { id: string; firstName: string; lastName: string }
}

interface AdminUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

const STATUS_LABEL: Record<string, string> = {
  pending:  'In attesa',
  approved: 'Approvata',
  rejected: 'Rifiutata',
}

const STATUS_CLASS: Record<string, string> = {
  pending:  'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}

const DAYS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']

const formatDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('it-IT')
const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function dateRange(from: string, to: string): string[] {
  const dates: string[] = []
  const cur = new Date(from + 'T00:00:00')
  const end = new Date(to + 'T00:00:00')
  while (cur <= end) {
    dates.push(toISO(cur))
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

export function PermessiPage() {
  const { user } = useAuth()
  const isLeader = user?.role === 'team_leader'
  const qc = useQueryClient()
  const today = new Date()
  const [calYear, setCalYear] = useState(today.getFullYear())
  const [calMonth, setCalMonth] = useState(today.getMonth())
  const [open, setOpen] = useState(false)
  const [leaveTypeId, setLeaveTypeId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [notes, setNotes] = useState('')
  const [referentId, setReferentId] = useState('')
  const [formError, setFormError] = useState('')
  const [pastDate, setPastDate] = useState('')
  const [checkinSuccess, setCheckinSuccess] = useState<string | null>(null)
  const pastInputRef = useRef<HTMLInputElement>(null)

  // Proprie richieste
  const { data: myRequests = [], isLoading } = useQuery<LeaveRequest[]>({
    queryKey: ['leave-requests', 'me'],
    queryFn: async () => (await api.get<LeaveRequest[]>('/leave-requests/me')).data,
  })

  // Richieste del gruppo (solo team_leader)
  const { data: teamRequests = [] } = useQuery<LeaveRequest[]>({
    queryKey: ['leave-requests', 'team'],
    queryFn: async () =>
      (await api.get<LeaveRequest[]>('/leave-requests')).data,
    enabled: isLeader,
  })

  // Presenze del gruppo per il mese corrente (solo team_leader)
  const calDateFrom = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01`
  const calDateTo = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${new Date(calYear, calMonth + 1, 0).getDate()}`
  const { data: teamAttendance = [] } = useQuery<AttendanceRecord[]>({
    queryKey: ['attendance', 'team', calDateFrom, calDateTo],
    queryFn: async () =>
      (await api.get<AttendanceRecord[]>('/attendance', {
        params: { dateFrom: calDateFrom, dateTo: calDateTo },
      })).data,
    enabled: isLeader,
  })

  const { data: leaveTypes = [] } = useQuery<LeaveType[]>({
    queryKey: ['leave-types'],
    queryFn: async () => (await api.get<LeaveType[]>('/leave-types')).data,
  })

  const { data: admins = [] } = useQuery<AdminUser[]>({
    queryKey: ['users', 'admins'],
    queryFn: async () => (await api.get<AdminUser[]>('/users/admins')).data,
    enabled: isLeader,
  })

  const createMutation = useMutation({
    mutationFn: async (body: {
      leaveTypeId: string; dateFrom: string; dateTo: string; notes?: string; referentId?: string
    }) => api.post('/leave-requests', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leave-requests'] })
      closeForm()
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.message ?? 'Errore nella creazione.')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/leave-requests/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leave-requests', 'me'] }),
  })

  // Map ISO date → { color, status } per il calendario personale
  const myDayMap = useMemo(() => {
    const m = new Map<string, { color: string; status: string }>()
    myRequests
      .filter((r) => r.status !== 'rejected')
      .forEach((r) => {
        dateRange(r.dateFrom, r.dateTo).forEach((d) => {
          m.set(d, { color: r.leaveType.color, status: r.status })
        })
      })
    return m
  }, [myRequests])

  // Map ISO date → array eventi per il calendario di gruppo
  // Ogni evento: { name, color, kind: 'leave' | 'present', status? }
  const teamDayMap = useMemo(() => {
    const m = new Map<string, Array<{ name: string; color: string; kind: 'leave' | 'present'; status?: string }>>()

    // Permessi (pending/approved)
    teamRequests
      .filter((r) => r.status !== 'rejected')
      .forEach((r) => {
        const name = r.technician
          ? `${r.technician.firstName} ${r.technician.lastName}`
          : 'Tecnico'
        dateRange(r.dateFrom, r.dateTo).forEach((d) => {
          const arr = m.get(d) ?? []
          arr.push({ name, color: r.leaveType.color, kind: 'leave', status: r.status })
          m.set(d, arr)
        })
      })

    // Presenze effettive (check-in / ticket)
    teamAttendance.forEach((rec) => {
      const name = rec.technician
        ? `${rec.technician.firstName} ${rec.technician.lastName}`
        : 'Tecnico'
      const arr = m.get(rec.date) ?? []
      // Aggiunge solo se non c'è già una voce "present" per questo tecnico
      if (!arr.some((e) => e.kind === 'present' && e.name === name)) {
        arr.push({ name, color: '#22C55E', kind: 'present' }) // verde presenza
        m.set(rec.date, arr)
      }
    })

    return m
  }, [teamRequests, teamAttendance])

  const calendarDays = useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1)
    const startDow = (firstDay.getDay() + 6) % 7
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate()
    const cells: (string | null)[] = [
      ...Array(startDow).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => toISO(new Date(calYear, calMonth, i + 1))),
    ]
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [calYear, calMonth])

  const monthLabel = new Date(calYear, calMonth, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
  const todayISO = toISO(today)

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1) } else setCalMonth(m => m - 1) }
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1) } else setCalMonth(m => m + 1) }

  const closeForm = () => {
    setOpen(false); setLeaveTypeId(''); setDateFrom(''); setDateTo('')
    setNotes(''); setReferentId(''); setFormError('')
  }

  // Check-in presenza — carica tutto il mese visualizzato nel calendario
  const attDateFrom = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01`
  const attDateTo = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${new Date(calYear, calMonth + 1, 0).getDate()}`
  const { data: myAttendance = [] } = useQuery<{ id: string; date: string; source: string; checkInTime: string | null }[]>({
    queryKey: ['attendance-me-permessi', attDateFrom, attDateTo],
    queryFn: async () =>
      (await api.get('/attendance/me', { params: { dateFrom: attDateFrom, dateTo: attDateTo } })).data,
  })
  const myAttendanceSet = useMemo(() => new Set(myAttendance.map((r) => r.date)), [myAttendance])
  const todayRec = myAttendance.find((r) => r.date === todayISO)
  const alreadyCheckedIn = todayRec != null && (todayRec.source === 'manual' || todayRec.source === 'mixed')

  const checkinMutation = useMutation({
    mutationFn: (date?: string) => api.post('/attendance/checkin', date ? { date } : {}),
    onSuccess: (_data, date) => {
      void qc.invalidateQueries({ queryKey: ['attendance-me-permessi'] })
      const label = date
        ? new Date(date + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })
        : 'oggi'
      setCheckinSuccess(`Presenza dichiarata per ${label}`)
      setPastDate('')
      setTimeout(() => setCheckinSuccess(null), 4000)
    },
    onError: (err: any) => {
      alert(err?.response?.data?.message ?? 'Errore durante la timbratura.')
    },
  })

  const handleSubmit = () => {
    setFormError('')
    if (!leaveTypeId || !dateFrom || !dateTo) {
      setFormError('Tipo permesso, data inizio e data fine sono obbligatori.')
      return
    }
    if (dateFrom > dateTo) {
      setFormError('La data di inizio deve essere precedente alla data di fine.')
      return
    }
    createMutation.mutate({
      leaveTypeId, dateFrom, dateTo,
      notes: notes || undefined,
      referentId: isLeader && referentId ? referentId : undefined,
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isLeader ? 'Presenze & permessi' : 'I miei permessi'}
        breadcrumb={[{ label: isLeader ? 'Presenze & permessi' : 'Permessi' }]}
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nuova richiesta
          </Button>
        }
      />

      {/* Banner successo */}
      {checkinSuccess && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-2 text-green-800 text-sm font-medium">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {checkinSuccess}
        </div>
      )}

      {/* Card dichiarazione presenza */}
      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] divide-y divide-[var(--fc-border)]">
        {/* Riga 1 — oggi */}
        <div className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {alreadyCheckedIn
              ? <CheckCircle2 className="h-7 w-7 text-green-500 flex-shrink-0" />
              : <Clock className="h-7 w-7 text-[var(--fc-primary)] flex-shrink-0" />
            }
            <div>
              <p className="font-semibold text-[var(--fc-foreground)]">
                {alreadyCheckedIn ? 'Presenza dichiarata oggi' : 'Dichiara presenza oggi'}
              </p>
              <p className="text-sm text-[var(--fc-foreground-muted)]">
                {alreadyCheckedIn
                  ? `Timbrato alle ${todayRec?.checkInTime ? new Date(todayRec.checkInTime).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '—'}`
                  : new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          <Button
            onClick={() => checkinMutation.mutate(undefined)}
            disabled={alreadyCheckedIn || checkinMutation.isPending}
            variant={alreadyCheckedIn ? 'ghost' : 'primary'}
          >
            {checkinMutation.isPending ? 'Salvataggio...' : alreadyCheckedIn ? 'Già timbrato' : 'Timbra presenza'}
          </Button>
        </div>

        {/* Riga 2 — giorno precedente */}
        <div className="p-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-medium text-[var(--fc-foreground)] text-sm">Dichiara giorno precedente</p>
            <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5">Seleziona un giorno passato in cui non hai timbrato</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={pastInputRef}
              type="date"
              value={pastDate}
              max={new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().split('T')[0]}
              onChange={(e) => setPastDate(e.target.value)}
              className="rounded-md border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-primary)]"
            />
            <Button
              onClick={() => { if (pastDate) checkinMutation.mutate(pastDate) }}
              disabled={!pastDate || checkinMutation.isPending}
              variant="secondary"
            >
              {checkinMutation.isPending ? 'Salvataggio...' : 'Dichiara'}
            </Button>
          </div>
        </div>
      </div>

      {/* Calendario mensile — personale (tecnico) o di gruppo (team_leader) */}
      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="rounded p-1 hover:bg-[var(--fc-surface-overlay)] transition-colors" aria-label="Mese precedente">
            <ChevronLeft className="h-4 w-4 text-[var(--fc-foreground-muted)]" />
          </button>
          <span className="font-medium text-[var(--fc-foreground)] capitalize">{monthLabel}</span>
          <button onClick={nextMonth} className="rounded p-1 hover:bg-[var(--fc-surface-overlay)] transition-colors" aria-label="Mese successivo">
            <ChevronRight className="h-4 w-4 text-[var(--fc-foreground-muted)]" />
          </button>
        </div>

        <div className="grid grid-cols-7 mb-1">
          {DAYS_IT.map((d) => (
            <div key={d} className="text-center text-xs font-medium text-[var(--fc-foreground-muted)] py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((iso, idx) => {
            if (!iso) return <div key={idx} />
            const isToday = iso === todayISO

            if (isLeader) {
              const entries = teamDayMap.get(iso) ?? []
              const tooltip = entries.map(e =>
                e.kind === 'present'
                  ? `✓ ${e.name} (presente)`
                  : `${e.name} (${STATUS_LABEL[e.status ?? 'pending']})`
              ).join('\n')
              return (
                <div
                  key={iso}
                  className="relative flex flex-col items-center rounded-md min-h-[2.5rem] pt-1 pb-0.5 text-sm"
                  title={tooltip || undefined}
                >
                  <span className={['font-medium text-xs', isToday ? 'text-[var(--fc-primary)]' : 'text-[var(--fc-foreground)]'].join(' ')}>
                    {new Date(iso + 'T00:00:00').getDate()}
                  </span>
                  {entries.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-0.5 mt-0.5">
                      {entries.slice(0, 5).map((e, i) => (
                        <span
                          key={i}
                          // cerchio pieno = presente, cerchio con bordo = permesso
                          className={e.kind === 'present' ? 'inline-block h-1.5 w-1.5 rounded-full' : 'inline-block h-1.5 w-1.5 rounded-full border'}
                          style={{
                            backgroundColor: e.kind === 'present' ? e.color : (e.status === 'approved' ? e.color : 'transparent'),
                            borderColor: e.kind === 'leave' ? e.color : undefined,
                            opacity: e.status === 'pending' ? 0.5 : 1,
                          }}
                        />
                      ))}
                      {entries.length > 5 && (
                        <span className="text-[9px] text-[var(--fc-foreground-muted)]">+{entries.length - 5}</span>
                      )}
                    </div>
                  )}
                </div>
              )
            }

            // Calendario personale — read-only, solo visualizzazione
            const info = myDayMap.get(iso)
            const hasAttendance = myAttendanceSet.has(iso)
            const isFuture = iso > todayISO

            return (
              <div
                key={iso}
                className={[
                  'relative flex flex-col items-center justify-center rounded-md h-10 text-sm',
                  isFuture ? 'opacity-30' : '',
                ].join(' ')}
                style={info ? { backgroundColor: info.color + '22' } : undefined}
                title={hasAttendance ? 'Presenza dichiarata' : info ? `${info.status === 'approved' ? 'Permesso approvato' : 'Permesso in attesa'}` : undefined}
              >
                {/* pallino permesso — angolo in alto a destra */}
                {info && (
                  <span
                    className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: info.color, opacity: info.status === 'pending' ? 0.5 : 1 }}
                  />
                )}
                {/* pallino presenza — centro in basso */}
                {hasAttendance && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-green-500" />
                )}
                <span className={[
                  'font-medium text-xs',
                  isToday ? 'text-[var(--fc-primary)] font-bold' : 'text-[var(--fc-foreground)]',
                ].join(' ')}>
                  {new Date(iso + 'T00:00:00').getDate()}
                </span>
              </div>
            )
          })}
        </div>

        {/* Legenda */}
        <div className="mt-3 flex flex-wrap gap-3">
          <div className="flex items-center gap-1.5 text-xs text-[var(--fc-foreground-muted)]">
            <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
            {isLeader ? 'Presente' : 'Presenza dichiarata'}
          </div>
          {leaveTypes.map((t) => (
            <div key={t.id} className="flex items-center gap-1.5 text-xs text-[var(--fc-foreground-muted)]">
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />
              {t.name} (approvato)
            </div>
          ))}
          <div className="flex items-center gap-1.5 text-xs text-[var(--fc-foreground-muted)]">
            <span className="inline-block h-2 w-2 rounded-full bg-gray-300 opacity-50" />
            Permesso in attesa
          </div>
        </div>

        {/* Situazione oggi (solo team_leader) */}
        {isLeader && (() => {
          const todayEntries = teamDayMap.get(todayISO) ?? []
          if (todayEntries.length === 0) return null
          const presenti = todayEntries.filter(e => e.kind === 'present')
          const assenti  = todayEntries.filter(e => e.kind === 'leave')
          return (
            <div className="mt-3 border-t border-[var(--fc-border)] pt-3 space-y-2">
              {presenti.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Oggi presenti:</p>
                  <div className="flex flex-wrap gap-2">
                    {presenti.map((e, i) => (
                      <span key={i} className="flex items-center gap-1.5 text-xs rounded-full px-2 py-0.5 bg-green-50 text-green-700">
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                        {e.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {assenti.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Oggi assenti:</p>
                  <div className="flex flex-wrap gap-2">
                    {assenti.map((e, i) => (
                      <span key={i} className="flex items-center gap-1.5 text-xs rounded-full px-2 py-0.5 bg-[var(--fc-surface-overlay)]">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: e.color }} />
                        {e.name}
                        <span className="text-[var(--fc-foreground-muted)]">({STATUS_LABEL[e.status ?? 'pending']})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })()}
      </div>

      {/* Lista richieste proprie */}
      <div>
        <h2 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Le mie richieste</h2>
        {isLoading ? (
          <div className="py-8 text-center text-sm text-[var(--fc-foreground-muted)]">Caricamento...</div>
        ) : myRequests.length === 0 ? (
          <div className="py-8 text-center text-sm text-[var(--fc-foreground-muted)]">
            Nessuna richiesta. Clicca &quot;Nuova richiesta&quot; per iniziare.
          </div>
        ) : (
          <div className="space-y-3">
            {myRequests.map((req) => {
              const days = Math.ceil((new Date(req.dateTo).getTime() - new Date(req.dateFrom).getTime()) / 86400000) + 1
              return (
                <div key={req.id} className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-1 inline-block h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: req.leaveType.color }} />
                    <div>
                      <p className="font-medium text-[var(--fc-foreground)]">{req.leaveType.name}</p>
                      <p className="text-sm text-[var(--fc-foreground-muted)]">
                        {formatDate(req.dateFrom)} → {formatDate(req.dateTo)} ({days} giorn{days === 1 ? 'o' : 'i'})
                      </p>
                      {req.notes && <p className="text-xs text-[var(--fc-foreground-muted)] mt-1">Nota: {req.notes}</p>}
                      {req.status === 'rejected' && req.rejectionReason && (
                        <p className="text-xs text-red-600 mt-1">Motivo rifiuto: {req.rejectionReason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASS[req.status]}`}>
                      {STATUS_LABEL[req.status]}
                    </span>
                    {req.status === 'pending' && (
                      <button
                        onClick={() => deleteMutation.mutate(req.id)}
                        disabled={deleteMutation.isPending}
                        className="text-[var(--fc-foreground-muted)] hover:text-red-500 transition-colors"
                        title="Cancella richiesta"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal nuova richiesta */}
      <Modal open={open} onClose={closeForm} title="Nuova richiesta permesso" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">Tipo permesso *</label>
            <select
              value={leaveTypeId}
              onChange={(e) => setLeaveTypeId(e.target.value)}
              className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-primary)]"
            >
              <option value="">Seleziona...</option>
              {leaveTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input label="Dal *" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input label="Al *" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          <Input
            label="Note (opzionale)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="es. Vacanza estiva"
          />

          {/* Referente admin — solo per team_leader */}
          {isLeader && (
            <div>
              <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
                Referente admin <span className="text-[var(--fc-foreground-muted)] font-normal">(chi deve approvare)</span>
              </label>
              <select
                value={referentId}
                onChange={(e) => setReferentId(e.target.value)}
                className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-primary)]"
              >
                <option value="">Admin di default</option>
                {admins.map((a) => (
                  <option key={a.id} value={a.id}>{a.firstName} {a.lastName}</option>
                ))}
              </select>
            </div>
          )}

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeForm}>Annulla</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Invio...' : 'Invia richiesta'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
